
import React from 'react';
import { Check, X, Crown, Zap, Shield, Infinity, Sparkles } from 'lucide-react';
import { Button } from './Button';
import { STRIPE_LINKS, FREE_TIER_LIMIT, FREE_AI_LIMIT, FREE_ROUTINE_LIMIT } from '../constants';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string | null;
  userId?: string;
  triggerReason?: string;
}

export const PricingModal: React.FC<PricingModalProps> = ({ 
  isOpen, 
  onClose, 
  userEmail, 
  userId,
  triggerReason 
}) => {
  if (!isOpen) return null;

  const handleCheckout = () => {
    // Stripe Payment Links use '?' for the first param
    const baseUrl = STRIPE_LINKS.checkout;
    const params = new URLSearchParams();
    
    if (userEmail) params.append('prefilled_email', userEmail);
    if (userId) params.append('client_reference_id', userId);

    const queryString = params.toString();
    const finalUrl = queryString ? `${baseUrl}?${queryString}` : baseUrl;
    
    window.open(finalUrl, '_blank');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in overflow-y-auto">
      <div className="relative bg-white dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row border border-gray-200 dark:border-dark-border">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 bg-black/10 hover:bg-black/20 dark:bg-white/10 dark:hover:bg-white/20 rounded-full transition-colors"
        >
          <X size={20} className="text-gray-600 dark:text-gray-300" />
        </button>

        {/* LEFT SIDE: Visual & Hook */}
        <div className="bg-gradient-to-br from-blue-600 to-indigo-800 text-white p-8 md:w-2/5 flex flex-col justify-between relative overflow-hidden">
           <div className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2 blur-3xl"></div>
           <div className="absolute bottom-0 right-0 w-64 h-64 bg-purple-500/20 rounded-full translate-x-1/2 translate-y-1/2 blur-3xl"></div>

           <div className="relative z-10">
             <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-xl flex items-center justify-center mb-6 shadow-lg border border-white/30">
                <Crown size={24} className="text-yellow-300" fill="currentColor" />
             </div>
             <h2 className="text-3xl font-bold mb-2">Upgrade to Pro</h2>
             <p className="text-blue-100 font-medium">Invest in your focus for less than the price of a coffee.</p>
             
             {triggerReason && (
               <div className="mt-6 p-4 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 animate-pulse">
                  <p className="text-sm font-bold text-yellow-300 mb-1">Unlock Required</p>
                  <p className="text-xs text-blue-50">{triggerReason}</p>
               </div>
             )}
           </div>

           <div className="relative z-10 mt-8 space-y-4">
              <div className="flex items-center gap-3">
                 <Shield className="text-blue-300 shrink-0" size={20} />
                 <p className="text-sm">Deep Focus Blocking protects your attention.</p>
              </div>
              <div className="flex items-center gap-3">
                 <Infinity className="text-blue-300 shrink-0" size={20} />
                 <p className="text-sm">Unlimited tasks & history. No boundaries.</p>
              </div>
              <div className="flex items-center gap-3">
                 <Sparkles className="text-blue-300 shrink-0" size={20} />
                 <p className="text-sm">Unlimited AI Magic Sort usage.</p>
              </div>
           </div>
        </div>

        {/* RIGHT SIDE: Comparison Table */}
        <div className="p-8 md:w-3/5 bg-white dark:bg-dark-surface text-gray-900 dark:text-dark-text flex flex-col">
           <h3 className="text-xl font-bold text-center mb-6">Choose your productivity level</h3>
           
           <div className="flex-1 overflow-x-auto">
             <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                     <th className="text-left py-3 font-medium text-gray-400">Features</th>
                     <th className="px-4 py-3 font-medium text-gray-500">Free</th>
                     <th className="px-4 py-3 font-bold text-blue-600 dark:text-blue-400 text-lg">Pro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                   <tr>
                      <td className="py-4 text-gray-600 dark:text-gray-300 font-medium">Active Tasks</td>
                      <td className="px-4 text-center text-gray-500">Max {FREE_TIER_LIMIT}</td>
                      <td className="px-4 text-center font-bold text-gray-900 dark:text-white flex justify-center"><Infinity size={18}/></td>
                   </tr>
                   <tr>
                      <td className="py-4 text-gray-600 dark:text-gray-300 font-medium">Daily Rituals</td>
                      <td className="px-4 text-center text-gray-500">Max {FREE_ROUTINE_LIMIT}</td>
                      <td className="px-4 text-center font-bold text-gray-900 dark:text-white">Unlimited</td>
                   </tr>
                   <tr>
                      <td className="py-4 text-gray-600 dark:text-gray-300 font-medium">Deep Focus</td>
                      <td className="px-4 text-center text-gray-500">Timer Only</td>
                      <td className="px-4 text-center font-bold text-gray-900 dark:text-white">Block Sites</td>
                   </tr>
                   <tr>
                      <td className="py-4 text-gray-600 dark:text-gray-300 font-medium">AI Magic Sort</td>
                      <td className="px-4 text-center text-gray-500">{FREE_AI_LIMIT} / day</td>
                      <td className="px-4 text-center font-bold text-gray-900 dark:text-white">Unlimited</td>
                   </tr>
                   <tr>
                      <td className="py-4 text-gray-600 dark:text-gray-300 font-medium">Analytics History</td>
                      <td className="px-4 text-center text-gray-500">7 Days</td>
                      <td className="px-4 text-center font-bold text-gray-900 dark:text-white">Lifetime</td>
                   </tr>
                   <tr>
                      <td className="py-4 text-gray-600 dark:text-gray-300 font-medium">Themes</td>
                      <td className="px-4 text-center text-gray-500">Classic Light</td>
                      <td className="px-4 text-center font-bold text-gray-900 dark:text-white">All Themes</td>
                   </tr>
                </tbody>
             </table>
           </div>

           <div className="mt-8 text-center">
              <div className="mb-4">
                 <span className="text-3xl font-bold text-gray-900 dark:text-white">$3.99</span>
                 <span className="text-gray-500 dark:text-gray-400"> / month</span>
              </div>
              
              <Button 
                onClick={handleCheckout} 
                size="lg" 
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-500/30 transform hover:-translate-y-0.5 transition-all text-lg h-14"
              >
                 Get Unlimited Access
              </Button>
              <p className="text-xs text-gray-400 mt-3">
                 Sync across all your devices. Cancel anytime.
              </p>
           </div>
        </div>

      </div>
    </div>
  );
};
